import React from 'react';
import { alpha, styled } from '@mui/material/styles';
import { Grid, Paper, Typography, Switch, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { Box, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { setupFirestoreEmulator, setupStorageEmulator, timestampToDate2 } from '../shows/utils';

setupFirestoreEmulator(db);
setupStorageEmulator(storage);

const CustomSwitch = styled(Switch)(({ theme }) => ({
    "& .MuiSwitch-switchBase.Mui-checked": {
        color: "#01DF3A",
        "&:hover": {
            backgroundColor: alpha("#01DF3A", theme.palette.action.hoverOpacity),
        },
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: "#01DF3A",
    },
    "& .MuiSwitch-switchBase": {
        color: "#FF4000",
        "&:hover": {
            backgroundColor: alpha("#FF4000", theme.palette.action.hoverOpacity),
        },
    },
    "& .MuiSwitch-track": {
        backgroundColor: "#FF4000",
    },
}));

export default function ShowsTable({ setMessage, setSeverity, setSnackbarOpen }) {
    const [shows, setShows] = React.useState([]);
    const [confirmShow, setConfirmShow] = React.useState(null);
    const [deleting, setDeleting] = React.useState(false);

    React.useEffect(() => {
        async function fetchShows() {
            try {
                const snapshot = await getDocs(collection(db, 'shows'));
                const loaded = snapshot.docs
                    .map(d => ({ ...d.data(), _id: d.id }))
                    .sort((a, b) => b.date.seconds - a.date.seconds);
                setShows(loaded);
            } catch (error) {
                console.error('Failed to fetch shows:', error);
            }
        }
        fetchShows();
    }, []);

    const handleToggle = async (show) => {
        const newEnabled = show.enabled === false ? true : false;
        try {
            await updateDoc(doc(db, 'shows', show._id), { enabled: newEnabled });
            setShows(prev => prev.map(s => s._id === show._id ? { ...s, enabled: newEnabled } : s));
            setMessage(`'${show.venue}' (${timestampToDate2(show.date)}) ${newEnabled ? 'enabled' : 'disabled'}`);
            setSeverity('success');
        } catch (error) {
            console.error('Failed to update show:', error);
            setMessage(`Failed to update '${show.venue}'`);
            setSeverity('error');
        }
        setSnackbarOpen(true);
    };

    const handleDeleteConfirm = async () => {
        const show = confirmShow;
        setDeleting(true);
        try {
            // Delete all files in Cloud Storage under shows/{showPath}/
            const folderRef = ref(storage, `shows/${show._id}`);
            const listed = await listAll(folderRef);
            await Promise.all(listed.items.map(item => deleteObject(item)));

            // Delete the Firestore document
            await deleteDoc(doc(db, 'shows', show._id));

            setShows(prev => prev.filter(s => s._id !== show._id));
            setMessage(`'${show.venue}' (${timestampToDate2(show.date)}) deleted`);
            setSeverity('success');
        } catch (error) {
            console.error('Failed to delete show:', error);
            setMessage(`Failed to delete '${show.venue}'`);
            setSeverity('error');
        }
        setDeleting(false);
        setConfirmShow(null);
        setSnackbarOpen(true);
    };

    return (
        <>
        <Paper square={false} variant="outlined" sx={{ display: 'flex', flexGrow: 1, bgcolor: '#3a3f43', p: 3, mt: 3 }}>
            <Grid container>
                <Grid item xs={12}>
                    <Typography gutterBottom variant="h6">
                        Shows
                    </Typography>
                </Grid>
                <Grid container item justifyContent="center">
                    <TableContainer component={Box}>
                        <Table sx={{ backgroundColor: 'transparent' }} size="medium">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: 'white' }}>Date</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Venue</TableCell>
                                    <TableCell sx={{ color: 'white', '@media (max-width:650px)': { display: 'none' } }}>City, State</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Enabled</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Delete</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {shows.map((show, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell sx={{ border: 0, color: 'white' }}>
                                            {timestampToDate2(show.date)}
                                        </TableCell>
                                        <TableCell sx={{ border: 0, color: 'white' }}>
                                            {show.venue}
                                        </TableCell>
                                        <TableCell sx={{ border: 0, color: 'white', '@media (max-width:650px)': { display: 'none' } }}>
                                            {show.city}, {show.state}
                                        </TableCell>
                                        <TableCell sx={{ border: 0 }}>
                                            <CustomSwitch
                                                checked={show.enabled !== false}
                                                onChange={() => handleToggle(show)}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ border: 0 }}>
                                            <IconButton
                                                onClick={() => setConfirmShow(show)}
                                                sx={{ color: '#FF4000' }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Paper>

        <Dialog open={!!confirmShow} onClose={() => !deleting && setConfirmShow(null)}>
            <DialogTitle sx={{ color: 'white', bgcolor: '#3a3f43' }}>Delete Show</DialogTitle>
            <DialogContent sx={{ bgcolor: '#3a3f43' }}>
                <DialogContentText sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Delete <strong>{confirmShow?.venue}</strong> ({confirmShow ? timestampToDate2(confirmShow.date) : ''})? This will permanently remove the show and all associated audio and image files. This cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ bgcolor: '#3a3f43' }}>
                <Button onClick={() => setConfirmShow(null)} disabled={deleting} sx={{ color: 'white' }}>
                    Cancel
                </Button>
                <Button onClick={handleDeleteConfirm} disabled={deleting} sx={{ color: '#FF4000' }}>
                    {deleting ? 'Deleting...' : 'Delete'}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}
